/**
 * Fase post-«Empezar» para Historias: muestra los recursos de la historia (texto, audio
 * karaoke, vídeo). El control de la barra inferior — incluida la
 * transformación del botón principal y el play/pause del karaoke — vive en
 * el padre (`StoryDetailPage` → `StoryMorphBar`) para preservar la
 * instancia entre intro y consuming y poder animar el morph.
 *
 * El `KaraokePlayer` arranca solo al montar (autoPlay) y sube su bloque bajo
 * el header del layout — mismo gesto visual que las burbujas al pulsar
 * Empezar en una conversación. El play/pause se registra en el
 * `StoryAudioContext` y la barra inferior lo dispara.
 */

import { useCallback, useRef } from 'react';
import { ResourcesTab } from '../StoryPage/components/ResourcesTab';
import { markStoryAudioListened } from '@/services/api/stories.api';
import type { Story } from '@/types/story.types';

export interface StoryStartedFlowProps {
  story: Story;
  /** El alumno ya escuchó el audio completo en una sesión anterior (dato del backend). */
  audioAlreadyListened?: boolean;
}

export function StoryStartedFlow({ story, audioAlreadyListened = false }: StoryStartedFlowProps) {
  // Primera escucha completa → se persiste en el backend (fire-and-forget) para
  // que en próximas visitas no sea obligatorio re-escuchar. Una sola llamada por
  // montaje; si ya estaba escuchada, no hay nada que marcar.
  const markedRef = useRef(false);
  const handleAudioFinished = useCallback(() => {
    if (audioAlreadyListened || markedRef.current) return;
    markedRef.current = true;
    markStoryAudioListened(story.id).catch(() => {
      // Sin red / error transitorio: no rompemos la escucha; se reintentará
      // la próxima vez que termine el audio en otra visita.
      markedRef.current = false;
    });
  }, [audioAlreadyListened, story.id]);

  return (
    <ResourcesTab
      resources={story.resources}
      onAudioFinished={handleAudioFinished}
      audioAutoPlay={!audioAlreadyListened}
      audioInitiallyListened={audioAlreadyListened}
    />
  );
}
